import React from 'react';
import { motion } from 'framer-motion';
import { Star, ArrowRight, Sparkles, Shield, Clock, Gem, Quote, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Galaxy from '../components/cosmic/Galaxy';

const Home: React.FC = () => {
  return (
    <div className="relative overflow-hidden noise-overlay">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Galaxy 
            density={1.5} 
            speed={0.3} 
            hueShift={260} 
            saturation={0.4}
            glowIntensity={0.5}
            mouseInteraction={true}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/10 via-midnight/60 to-midnight z-1" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <span className="inline-block px-6 py-2 mb-8 text-[10px] font-black tracking-[0.3em] uppercase border border-gold/20 rounded-full text-gold bg-gold/5 backdrop-blur-xl">
              Est. 2004 • Trusted by 500k+ Souls
            </span>
            <h1 className="text-6xl md:text-8xl font-serif font-bold mb-10 leading-[1.1] tracking-tight">
              Unlock the Secrets of <br />
              <span className="text-gold italic font-medium">Your Celestial Destiny</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed font-light">
              AstroVidya.net bridges ancient Vedic wisdom with modern precision, providing personalized insights to guide your spiritual and material path.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link to="/services/birth-chart" className="btn-primary group">
                Get Your Birth Chart
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/shop" className="btn-secondary">
                Explore Zodiac Jewelry
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-2"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500">Scroll</span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-gold to-transparent" />
        </motion.div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 border-y border-white/5 bg-midnight-light/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-24 opacity-40 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-700">
            {['Vogue', 'Elle', 'Cosmopolitan', 'Harper\'s Bazaar', 'The Times'].map((brand) => (
              <span key={brand} className="text-xl font-serif font-bold tracking-widest text-white">{brand}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 bg-midnight relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-24">
            <h2 className="section-title">The AstroVidya Standard</h2>
            <p className="section-subtitle">We combine mathematical precision with spiritual intuition to deliver the most accurate astrological guidance available today.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                icon: <Shield className="w-8 h-8 text-gold" />,
                title: "Certified Expertise",
                desc: "Our panel consists of PhD-level Vedic scholars and certified Western astrologers with decades of practice."
              },
              {
                icon: <Sparkles className="w-8 h-8 text-gold" />,
                title: "Hyper-Personalized",
                desc: "We don't use generic templates. Every reading is calculated to the exact second and coordinate of your birth."
              },
              {
                icon: <Gem className="w-8 h-8 text-gold" />,
                title: "Ethical Craftsmanship",
                desc: "Our jewelry is hand-crafted using ethically sourced gemstones, energized through traditional Vedic rituals."
              }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-10"
              >
                <div className="w-16 h-16 rounded-2xl bg-gold/5 border border-gold/10 flex items-center justify-center mb-8">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-serif font-bold mb-4">{feature.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-light">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-32 bg-midnight-light/20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-end justify-between mb-20 gap-6">
            <div className="max-w-xl">
              <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">Celestial Offerings</h2>
              <p className="text-gray-400 font-light">Discover our range of personalized services and hand-crafted products designed to align your soul with the cosmic rhythm.</p>
            </div>
            <Link to="/shop" className="text-gold hover:text-white transition-all duration-300 flex items-center text-[10px] font-bold uppercase tracking-[0.3em] group">
              View All Collection <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              {
                title: "Birth Chart Reading",
                desc: "A comprehensive 40-page digital blueprint of your soul's journey.",
                img: "https://cdn.pixabay.com/photo/2015/10/17/19/30/astrology-993127_1280.jpg",
                link: "/services/birth-chart"
              },
              {
                title: "Zodiac Jewelry",
                desc: "Hand-crafted talismans designed to amplify your celestial energy.",
                img: "https://cdn.pixabay.com/photo/2014/07/18/13/49/jewels-396441_1280.jpg",
                link: "/shop"
              },
              {
                title: "Live Consultations",
                desc: "One-on-one video sessions with our master astrologers.",
                img: "https://cdn.pixabay.com/photo/2017/05/04/16/37/meeting-2284501_1280.jpg",
                link: "/services/consultations"
              }
            ].map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group relative overflow-hidden rounded-3xl aspect-[4/5] cursor-pointer"
              >
                <img 
                  src={service.img} 
                  alt={service.title} 
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/20 to-transparent opacity-90" />
                <div className="absolute bottom-0 left-0 right-0 p-10 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  <h3 className="text-3xl font-serif font-bold mb-3 text-white">{service.title}</h3>
                  <p className="text-gray-300 text-sm mb-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500 font-light">{service.desc}</p>
                  <Link to={service.link} className="inline-flex items-center text-gold font-bold text-[10px] uppercase tracking-[0.3em] group-hover:text-white transition-colors">
                    Explore <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 bg-midnight">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-24">
            <h2 className="section-title">Voices of the Cosmic Circle</h2>
            <p className="section-subtitle">Join thousands of satisfied seekers who have found clarity and purpose through AstroVidya.net.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              { name: "Priya Kapoor", role: "Entrepreneur", text: "The birth chart reading was eerily accurate. It gave me the confidence to launch my startup during a challenging transit." },
              { name: "David Miller", role: "Artist", text: "I wear my Leo Sun Medallion every day. The craftsmanship is exquisite, and I truly feel a shift in my creative energy." },
              { name: "Ananya Singh", role: "Software Engineer", text: "My consultation with Dr. Sharma was life-changing. He provided practical remedies that actually worked." }
            ].map((t, idx) => (
              <div key={idx} className="glass-card p-10 relative">
                <Quote className="absolute top-8 right-8 w-10 h-10 text-gold/10" />
                <div className="flex items-center space-x-1 mb-6">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-gold fill-gold" />)}
                </div>
                <p className="text-gray-400 italic mb-8 leading-relaxed font-light">"{t.text}"</p>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold font-bold">
                    {t.name[0]}
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">{t.name}</h4>
                    <p className="text-gray-600 text-[10px] uppercase tracking-widest">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-32 bg-midnight relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gold/5 blur-[150px] rounded-full pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-serif font-bold mb-8">Join the Cosmic Circle</h2>
            <p className="text-gray-400 mb-12 text-lg font-light leading-relaxed">Subscribe to our monthly horoscope newsletter tailored to your sun sign and receive celestial updates directly in your inbox.</p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input 
                type="email" 
                placeholder="Your celestial email" 
                className="flex-grow px-8 py-5 bg-white/5 border border-white/10 rounded-full focus:outline-none focus:border-gold transition-all text-white placeholder:text-gray-600"
                required
              />
              <button 
                type="submit" 
                className="btn-primary whitespace-nowrap"
              >
                Subscribe Now
              </button>
            </form>
            <div className="mt-10 flex items-center justify-center space-x-6 text-gray-600 text-[10px] uppercase tracking-[0.2em]">
              <div className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-gold" /> No Spam</div>
              <div className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-gold" /> Secure Data</div>
              <div className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-gold" /> Unsubscribe Anytime</div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
